/**
 * Mobile push bootstrap for Capacitor builds.
 *
 * Why no `import ... from "@capacitor/..."`?
 * -------------------------------------------
 * The Capacitor plugin packages are NOT in the React app's
 * `package.json` — they live only in the dynamically-generated
 * Capacitor shell (`/app/capacitor-app/package.json`, see
 * `scripts/build_app.py`). When the native iOS/Android WebView loads
 * this React bundle, the bundle is identical to the one served as a
 * PWA on `ecommerce.localappstore.in`. So we cannot reference the
 * plugin packages at bundle time.
 *
 * Instead, we use the runtime **Capacitor JS bridge**:
 *   `window.Capacitor.Plugins.PushNotifications`
 *   `window.Capacitor.Plugins.Device`
 *   `window.Capacitor.Plugins.App`
 * which the native shell injects into the WebView automatically.
 *
 * On a plain web browser `window.Capacitor` is undefined → bootstrap
 * is a complete no-op.
 *
 * Permission state machine
 * ------------------------
 *   1. checkPermissions()              ← never triggers a prompt
 *   2. switch (state)
 *      - 'granted'                     → straight to register()
 *      - 'prompt' / 'prompt-with-rationale'
 *                                      → requestPermissions() (shows OS dialog)
 *                                        then re-check; register() iff granted
 *      - 'denied'                      → log + expose `openNotificationSettings`;
 *                                        DO NOT call requestPermissions() again
 *                                        (Android caps it after 2 denies; iOS
 *                                        permanently silences the prompt after
 *                                        the first deny — only Settings helps).
 */
import { api } from "./api";
import { tenantScopedLink } from "./notificationLink";
import { routerHistoryState } from "./backNavigation";

// Bump this any time the file changes meaningfully. Logged to the device
// console so the operator can immediately tell whether the WebView has
// loaded the latest bundle or is serving a cached older one. If the log
// line `[mobilePush] bundle v=…` doesn't appear with the current value,
// the device is running stale JS — force-reinstall the app.
const MOBILEPUSH_BUNDLE_V = "2026-10-iter117-native-settings";

// Log tag used everywhere so users grepping `adb logcat` / Xcode console can
// trivially filter the push lifecycle from everything else in the WebView.
const TAG = "[mobilePush]";
const log = (...a) => console.log(TAG, ...a);
const warn = (...a) => console.warn(TAG, ...a);

let _registered = false; // flipped true ONLY after backend ack returns ok
let _gotRegistrationEvent = false;  // any registration event (success OR error)

function getCap() {
  return typeof window !== "undefined" ? window.Capacitor : null;
}

function isNative() {
  const cap = getCap();
  return !!(cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform());
}

function getPlatform() {
  const cap = getCap();
  return cap && typeof cap.getPlatform === "function" ? cap.getPlatform() : "web";
}

async function safe(fn, label) {
  try {
    const out = await fn();
    return out;
  } catch (e) {
    warn(`${label || "call"} threw:`, e?.message || e);
    return null;
  }
}

/**
 * Open the OS notification-settings page for this app, so a user whose
 * permission state is `denied` can re-enable pushes manually. UI surfaces
 * (e.g. a "Notifications are off — tap to enable" banner) should call this.
 *
 * Returns true when the navigation was successfully dispatched, false
 * otherwise (e.g. web browser, plugin missing). Never throws — the UI
 * caller can decide whether to fall back to a help-link.
 *
 * Uses the `capacitor-native-settings` plugin (`Plugins.NativeSettings`,
 * declared in scripts/build_app.py). No Capacitor 6 core plugin can open
 * system settings — `@capacitor/app` has no `openUrl`.
 *   Android: this app's notification settings (Android 8+); falls back to the
 *            app's details screen where that screen doesn't exist (7 and
 *            older, some OEM builds). Resolves when the user comes back.
 *   iOS:     this app's notification settings (iOS 16+), else its Settings page.
 * The plugin rejects when a screen cannot be opened. Apps built before the
 * plugin was added don't have it → false.
 */
export async function openNotificationSettings() {
  const Settings = getCap()?.Plugins?.NativeSettings;
  if (!isNative() || typeof Settings?.open !== "function") {
    warn("openNotificationSettings: NativeSettings plugin not in this app build — cannot open Settings");
    return false;
  }
  const open = async (optionAndroid) => {
    log("openNotificationSettings → NativeSettings.open", optionAndroid);
    const r = await safe(() => Settings.open({ optionAndroid, optionIOS: "appNotification" }), "NativeSettings.open");
    return r?.status === true;
  };
  if (await open("app_notification")) return true;
  if (getPlatform() !== "android") return false;
  return open("application_details");
}

/**
 * Public hook: returns the current OS notification permission state
 * (without triggering any prompt). Returns one of:
 *   'granted' | 'denied' | 'prompt' | 'prompt-with-rationale' | 'unavailable'
 * 'unavailable' is returned for non-Capacitor / missing-plugin shells.
 * UI banners use this to decide whether to nag the user.
 */
export async function getPushPermissionState() {
  if (!isNative()) return "unavailable";
  const Push = getCap()?.Plugins?.PushNotifications;
  if (!Push) return "unavailable";
  const r = await safe(() => Push.checkPermissions(), "Push.checkPermissions");
  return r?.receive || "unavailable";
}

// Same limits as the backend's RegisterDeviceIn — a longer value would get
// the whole registration rejected, and the token would never be stored.
const MAX_APP_ID = 128;
const MAX_APP_VERSION = 32;

function cleanText(value, max) {
  if (typeof value !== "string" && typeof value !== "number") return null;
  const s = String(value).trim();
  return s ? s.slice(0, max) : null;
}

/**
 * App id + version of the installed native app, from `@capacitor/app`'s
 * `App.getInfo()` → { id, version, build }. (`Device.getInfo()` has neither
 * field, which is why every registration used to be stored without them.)
 *
 *   appId      — Android application id / iOS bundle id
 *   appVersion — "1.0.5 (5)": versionName + versionCode on Android,
 *                CFBundleShortVersionString + CFBundleVersion on iOS. The
 *                build number is what Firebase reports for a token, so the
 *                two can be matched.
 *
 * Missing plugin or a failed call → nulls. Metadata is optional; it must never
 * stop the token from being registered.
 */
export async function readAppMetadata(cap) {
  const App = cap?.Plugins?.App;
  if (typeof App?.getInfo !== "function") return { appId: null, appVersion: null };
  const info = await safe(() => App.getInfo(), "App.getInfo");
  const version = cleanText(info?.version, MAX_APP_VERSION);
  const build = cleanText(info?.build, MAX_APP_VERSION);
  const both = version && build && version !== build ? `${version} (${build})` : null;
  return {
    appId: cleanText(info?.id, MAX_APP_ID),
    appVersion: both && both.length <= MAX_APP_VERSION ? both : version || build,
  };
}

/** Body for POST /devices/register. */
export function registrationPayload({ token, platform, deviceId, appId, appVersion }) {
  return {
    token,
    platform: platform === "ios" ? "ios" : "android",
    device_id: deviceId || null,
    app_version: appVersion || null,
    app_id: appId || null,
  };
}

export async function bootstrapMobilePush() {
  if (_registered) {
    log("already registered this session — skipping");
    return;
  }
  if (!isNative()) {
    log("not running in Capacitor (platform=" + getPlatform() + ") — skipping native push registration");
    return;
  }
  const cap = getCap();
  const Push = cap?.Plugins?.PushNotifications;
  if (!Push) {
    warn(
      "PushNotifications plugin not available in Capacitor.Plugins — " +
      "the installed native shell was built without @capacitor/push-notifications. " +
      "Re-run scripts/build_app.py and reinstall the app."
    );
    return;
  }
  const platform = getPlatform();
  log("bundle v=" + MOBILEPUSH_BUNDLE_V + " — if you don't see this line, the WebView has a stale bundle. Force-quit + reinstall the app to refresh.");
  log("bootstrap start platform=" + platform);

  // ----- 1. Check current permission state (NEVER triggers a prompt) ------
  const checkRes = await safe(() => Push.checkPermissions(), "Push.checkPermissions");
  const initial = checkRes?.receive || "unknown";
  log("checkPermissions →", initial);

  let state = initial;

  // ----- 2. Branch on state -------------------------------------------------
  if (state === "denied") {
    // Calling requestPermissions() again here is pointless: iOS NEVER
    // re-prompts and Android caps the prompt at 2 denies. The only
    // recovery is the user toggling it in Settings — expose the helper
    // and bail (don't lock _registered so a future Settings flip will
    // be picked up on next AuthContext.refresh()).
    warn(
      "permission state=denied — OS dialog will NOT be shown again. " +
      "Call openNotificationSettings() from a UI affordance to let the user re-enable."
    );
    return;
  }

  if (state === "prompt" || state === "prompt-with-rationale" || state === "unknown") {
    log("requestPermissions → showing OS dialog");
    const reqRes = await safe(() => Push.requestPermissions(), "Push.requestPermissions");
    state = reqRes?.receive || "unknown";
    log("requestPermissions →", state);
    if (state !== "granted") {
      warn("user did not grant notifications (state=" + state + ") — no token will be issued");
      return;
    }
  } else if (state !== "granted") {
    warn("unexpected permission state — bailing:", state);
    return;
  }

  // ----- 3. Permission granted — Android channel is created NATIVELY -----
  // Capacitor's JS `Push.createChannel({...})` cannot create an
  // audible channel — passing no `sound` makes it silent, and any
  // string is treated as a `res/raw/<name>` filename (not as the
  // system-default URI). Once a channel exists, Android forbids
  // changing its sound.
  //
  // So channel creation is done in native Java code (PushChannels, called
  // from MainApplication and MainActivity — generated by
  // `scripts/build_app.py::_patch_android_mainactivity_channel`) before
  // this JS code loads. It creates channel id `push_high` (the id every
  // backend push names) with `Settings.System.DEFAULT_NOTIFICATION_URI`
  // (or the platform-configured custom sound URI) at IMPORTANCE_HIGH.
  // Foreground pushes shown by the plugin use the same channel.
  //
  // We deliberately do NOT call `Push.createChannel` from here anymore
  // — it would race the native channel and on some Android versions
  // overwrite the importance back to DEFAULT (no heads-up).
  // iOS has no concept of channels; this branch is irrelevant there.

  // ----- 4. Gather device info -------------------------------------------
  const Device = cap?.Plugins?.Device;
  let deviceId;
  if (Device) {
    const idRes = await safe(() => Device.getId(), "Device.getId");
    deviceId = idRes?.identifier || idRes?.uuid;
  } else {
    log("Device plugin missing — registering without a device id");
  }
  const { appId, appVersion } = await readAppMetadata(cap);
  log("device", { deviceId: (deviceId || "").slice(-8), appId, appVersion });

  // ----- 4. Wire listeners BEFORE register() so the token isn't lost ------
  // The `registration` event ALSO fires on token rotation (FCM rotates
  // periodically; APNs on certain reinstalls / iOS upgrades). The same
  // POST /devices/register call handles both initial registration AND
  // refresh — the backend's unique index on `device_tokens.token`
  // upserts the row and re-associates it with the current tenant + user.
  await safe(
    () => Push.addListener("registration", async (tok) => {
      _gotRegistrationEvent = true;
      const value = tok?.value || tok?.token;
      if (!value) {
        warn("registration event with empty token", tok);
        return;
      }
      const tokSuffix = value.slice(-12);
      log("registration ← APNs/FCM issued a token (length=" + value.length + ", …" + tokSuffix + ")");
      const payload = registrationPayload({ token: value, platform, deviceId, appId, appVersion });
      log("POST /devices/register → request", {
        ...payload,
        token: "…" + tokSuffix,  // never log the full token
      });
      try {
        const resp = await api.post("/devices/register", payload);
        _registered = true;
        log("POST /devices/register → response status=" + (resp?.status || "?"), resp?.data || {});
        log("✓ device registration COMPLETE — backend now knows about this device");
      } catch (e) {
        warn(
          "✗ POST /devices/register FAILED: status=" + (e?.response?.status || "?"),
          " body=", e?.response?.data || e?.message,
          " — token was minted by APNs/FCM but never stored in backend; next refresh() will retry",
        );
      }
    }),
    "Push.addListener(registration)",
  );

  await safe(
    () => Push.addListener("registrationError", (err) => {
      _gotRegistrationEvent = true;
      // Stringify so the user can see the actual NSError code on iOS
      // (e.g. NSError 3010 = "no valid 'aps-environment' entitlement string found").
      let detail;
      try { detail = JSON.stringify(err); } catch { detail = String(err); }
      warn(
        "✗ registrationError ← APNs/FCM REJECTED this device. Detail:", detail,
        "\n  Most common iOS causes (in order):",
        "\n    1. Missing aps-environment entitlement in signed binary.",
        "\n       → run scripts/verify_ios_entitlements.sh on your .ipa.",
        "\n    2. Provisioning profile doesn't include Push Notifications.",
        "\n       → Apple Developer Portal → Identifiers → enable Push, regenerate profile.",
        "\n    3. APNs Authentication Key (.p8) not uploaded to Firebase Console.",
        "\n  Android causes:",
        "\n    1. com.google.gms.google-services plugin not applied in build.gradle.",
        "\n    2. google-services.json belongs to a different Firebase project.",
        "\n    3. SHA-1 fingerprint of signing cert not added to Firebase Android app.",
      );
    }),
    "Push.addListener(registrationError)",
  );

  await safe(
    () => Push.addListener("pushNotificationReceived", async (notif) => {
      // This fires when a push arrives WHILE THE APP IS IN FOREGROUND.
      // We log for observability but DO NOT manually display the
      // notification anymore.
      //
      // Why no manual display?
      //   • Android: Firebase Messaging Service automatically displays
      //     the FCM payload's `notification` block on all app states
      //     (foreground / background / killed). It also fires this
      //     listener. If we then schedule a LocalNotification, the user
      //     sees TWO duplicates of the same push.
      //   • iOS: scripts/build_app.py now sets
      //     `PushNotifications.presentationOptions = ["badge","sound","alert"]`
      //     in capacitor.config.json, which tells UNUserNotificationCenter
      //     to display the banner in foreground too. Without that
      //     option iOS used to suppress foreground banners, which is
      //     why this code originally mirrored via LocalNotifications.
      //
      // If you ever need to display a custom UI (e.g. an in-app toast
      // instead of an OS banner), do it HERE — but do NOT call
      // Push.createChannel / LocalNotifications.schedule with the same
      // title+body because Firebase has already shown a banner.
      log(
        "pushNotificationReceived (FOREGROUND) ← title=",
        notif?.title,
        " body=",
        notif?.body,
        " data=",
        notif?.data,
        " id=",
        notif?.id,
      );
    }),
    "Push.addListener(pushNotificationReceived)",
  );

  // Fires when the user TAPS a notification (background or terminated).
  // Capacitor invokes this AFTER the system has launched / resumed the
  // app, so the React tree is mounted by the time we read the payload.
  // Use the data payload to deep-link (e.g. `data.url` → react-router).
  await safe(
    () => Push.addListener("pushNotificationActionPerformed", (action) => {
      log(
        "pushNotificationActionPerformed (TAP) ← actionId=",
        action?.actionId,
        " notification=",
        action?.notification,
      );
      const rawUrl = action?.notification?.data?.url;
      if (rawUrl && typeof window !== "undefined") {
        // The link is a store-relative path ("/shop"). The app runs under
        // `/t/<slug>/`, so put that prefix back — navigating to the bare
        // path leaves the router's basename and renders a blank screen.
        const url = tenantScopedLink(rawUrl, window.location.pathname);
        // Internal route → soft navigate; external → window.location.
        if (url.startsWith("/")) {
          try {
            // Router-shaped state, so the header back button keeps working.
            window.history.pushState(routerHistoryState(window.history.state), "", url);
            window.dispatchEvent(new PopStateEvent("popstate"));
          } catch (e) {
            window.location.href = url;
          }
        } else {
          window.location.href = url;
        }
      }
    }),
    "Push.addListener(pushNotificationActionPerformed)",
  );

  // ----- 5. Kick off APNs / FCM token issuance ----------------------------
  log("Push.register() → asking APNs/FCM for a token (this is async; result arrives in a listener)");
  _gotRegistrationEvent = false;
  const regCall = await safe(() => Push.register(), "Push.register");
  if (regCall === null) {
    warn("Push.register() threw — no token will arrive");
  } else {
    log("Push.register() returned (sync); listeners will fire when APNs/FCM responds");
  }

  // ----- 6. Silent-failure watchdog ---------------------------------------
  //
  // After Push.register(), iOS / Android should fire EITHER `registration`
  // (success) OR `registrationError` (failure) within a few seconds.
  // The pathological case is when NEITHER fires — APNs / FCM swallow the
  // call silently. Two-stage watchdog gives the operator fast feedback:
  //
  //   • 5s  → early-warning, prints "no response yet"
  //   • 30s → comprehensive diagnostic with remediation steps
  //
  // 5 seconds covers the 99th percentile of healthy APNs round-trips
  // (typically 0.5–3s). 30 seconds covers slow mobile networks.
  setTimeout(() => {
    if (_gotRegistrationEvent) return;
    warn(
      "⏱ 5s after Push.register() — no `registration` or `registrationError` " +
      "event yet. Healthy APNs typically responds within 1-3s. " +
      "Waiting 25 more seconds before the full diagnostic fires. " +
      "If you see this line and the next watchdog ALSO fires, the IPA is " +
      "almost certainly missing `aps-environment` in the SIGNED binary.",
    );
  }, 5_000);
  setTimeout(async () => {
    if (_gotRegistrationEvent) return;  // happy path
    const currentState = await safe(() => Push.checkPermissions(), "Push.checkPermissions(watchdog)");
    warn(
      "⚠ SILENT FAILURE WATCHDOG: 30s after Push.register(), neither " +
      "`registration` nor `registrationError` event has fired. " +
      "This means APNs/FCM never responded to the register call. " +
      "Diagnostic state: permission=" + (currentState?.receive || "?") +
      ", platform=" + platform + ", appId=" + (appId || "?") +
      ".\n\n" +
      "Most likely causes (iOS):\n" +
      "  1. App is missing the `aps-environment` entitlement in the SIGNED binary.\n" +
      "     The entitlements file might be in source but Xcode failed to embed it.\n" +
      "     → On your Mac, run: scripts/verify_ios_entitlements.sh path/to/App.ipa\n" +
      "  2. Provisioning profile doesn't include the Push Notifications capability.\n" +
      "     → Apple Developer Portal → Identifiers → " + (appId || "<bundle-id>") + "\n" +
      "     → Enable Push Notifications → regenerate profile → re-archive.\n" +
      "  3. Device has no network connection.\n" +
      "     → Verify on the device: Settings → Cellular / Wi-Fi shows data.\n" +
      "  4. APNs is currently unreachable from the device's network\n" +
      "     (firewall / corporate proxy / DNS).\n" +
      "     → Try a different network (mobile vs Wi-Fi).\n" +
      "Most likely causes (Android):\n" +
      "  1. google-services.json absent or for a different Firebase project.\n" +
      "  2. com.google.gms.google-services plugin not applied — see build.gradle.\n" +
      "  3. Device is missing Google Play Services entirely (China/AOSP builds).",
    );
  }, 30_000);
  // Token (or error) will arrive asynchronously in the listeners above.
}
